import {
	Bell,
	ChartColumn,
	ClipboardList,
	Code,
	Compass,
	CreditCard,
	Fingerprint,
	KeyRound,
	LayoutGrid,
	MessageSquareText,
	Plug,
	Send,
	SlidersHorizontal,
	User,
	Users,
} from "lucide-react";
import type { ComponentType } from "react";
import { settings } from "~/core/paths";

/**
 * The settings page's sections, and the only names that reach one.
 *
 * There were three settings surfaces — the panel's account dialog, the brand's
 * settings screen and the Agent's upstream `/settings` — and three
 * vocabularies, so this table used to carry the other two as aliases: the
 * upstream's `teammates`, `billing`, `general`. Every
 * link written in them has been rewritten to the section it meant, and a name
 * no section owns now resolves to `null`, because a caller deciding whether to
 * *offer* a destination needs to know there is none.
 *
 * The three groups are three questions decided by different people: a member
 * changes their own theme, an admin buys a seat, a brand editor sets the voice.
 * A single flat list made every one of those look equally available to
 * everybody.
 */
type SettingsGroup = "personal" | "account" | "workspace";

/**
 * Upstream's other workspace sections — custom tools, MCP servers, sandboxes,
 * Chat API keys — are deliberately absent. Their screens exist in the Agent
 * tree and their endpoints do not: the Rust gateway serves `/agent/credentials`
 * and 404s the rest, so listing them would put rows in the navigation that open
 * a page saying "Request failed with 404". Secrets is the one it answers.
 */
export type SettingsSection =
	| "profile"
	| "preferences"
	| "notifications"
	| "members"
	| "subscription"
	| "usage"
	| "integrations"
	| "publishing"
	| "identity"
	| "facts"
	| "audiences"
	| "measurement"
	| "site"
	| "filing"
	| "voice"
	| "secrets";

interface SettingsEntry {
	id: SettingsSection;
	group: SettingsGroup;
	/** A panel key. */
	label: string;
	/** A panel key: one line, the definition the page header's hint shows. */
	description: string;
	icon: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
	/**
	 * Whether the section is about a brand rather than the account. An account
	 * with no brand yet still opens this page — to pay, to invite somebody, to
	 * set its own language — and a row here that has no brand to write to is a
	 * row that opens a form with nothing behind it.
	 */
	brand?: true;
}

export const GROUPS: readonly { id: SettingsGroup; title: string }[] = [
	{ id: "personal", title: "settings.group.personal" },
	{ id: "account", title: "settings.group.account" },
	{ id: "workspace", title: "settings.group.workspace" },
];

export const SECTIONS: readonly SettingsEntry[] = [
	{
		id: "profile",
		group: "personal",
		label: "settings.section.profile",
		description: "settings.section.profileHint",
		icon: User,
	},
	{
		id: "preferences",
		group: "personal",
		label: "settings.section.preferences",
		description: "settings.section.preferencesHint",
		icon: SlidersHorizontal,
	},
	{
		id: "notifications",
		group: "personal",
		label: "settings.section.notifications",
		description: "settings.section.notificationsHint",
		icon: Bell,
	},
	{
		id: "members",
		group: "account",
		label: "settings.section.members",
		description: "settings.section.membersHint",
		icon: Users,
	},
	{
		id: "subscription",
		group: "account",
		label: "settings.section.subscription",
		description: "settings.section.subscriptionHint",
		icon: CreditCard,
	},
	{
		id: "usage",
		group: "account",
		label: "settings.section.usage",
		description: "settings.section.usageHint",
		icon: ChartColumn,
	},
	{
		id: "integrations",
		group: "workspace",
		label: "settings.section.integrations",
		description: "settings.section.integrationsHint",
		icon: Plug,
	},
	{
		id: "publishing",
		group: "workspace",
		label: "settings.section.publishing",
		description: "settings.section.publishingHint",
		icon: Send,
		brand: true,
	},
	{
		id: "identity",
		group: "workspace",
		label: "settings.section.identity",
		description: "settings.section.identityHint",
		icon: Fingerprint,
		brand: true,
	},
	{
		id: "facts",
		group: "workspace",
		label: "settings.section.facts",
		description: "settings.section.factsHint",
		icon: ClipboardList,
		brand: true,
	},
	{
		id: "measurement",
		group: "workspace",
		label: "settings.section.measurement",
		description: "settings.section.measurementHint",
		icon: Compass,
		brand: true,
	},
	{
		id: "site",
		group: "workspace",
		label: "settings.section.site",
		description: "settings.section.siteHint",
		icon: Code,
		brand: true,
	},
	{
		id: "audiences",
		group: "workspace",
		label: "settings.section.audiences",
		description: "settings.section.audiencesHint",
		icon: Users,
		brand: true,
	},
	{
		id: "filing",
		group: "workspace",
		label: "settings.section.filing",
		description: "settings.section.filingHint",
		icon: LayoutGrid,
		brand: true,
	},
	{
		id: "voice",
		group: "workspace",
		label: "settings.section.voice",
		description: "settings.section.voiceHint",
		icon: MessageSquareText,
		brand: true,
	},
	{
		id: "secrets",
		group: "workspace",
		label: "settings.section.secrets",
		description: "settings.section.secretsHint",
		icon: KeyRound,
	},
];

const BY_ID = new Map<string, SettingsEntry>(SECTIONS.map((entry) => [entry.id, entry]));

export function settingsEntry(name: string | null | undefined): SettingsEntry | null {
	return (name ? BY_ID.get(name) : undefined) ?? null;
}

/** The section a name is, or `null` when no section has that name. */
export function settingsSection(name: string | null | undefined): SettingsSection | null {
	return settingsEntry(name)?.id ?? null;
}

/**
 * Where a name lands on the settings page. Profile is the fallback because the
 * page has to open on something; it is not a translation of the name that was
 * asked for.
 */
export function settingsHref(brandId: string, name: string | null | undefined): string {
	return settings(brandId, settingsSection(name) ?? "profile");
}
